/**
 * AppShell — layout completo, DOM fiel a `mountLayout` [application.js:2015]:
 * <header> (Header) + <main><div id="content" class="wrapper"> + div.notice (ExpireBanner)
 * + div.mobile. Faz o bootstrap da sessão via useMe e trata os status como o legado:
 *  - MAINTENANCE_MODE → offlineWarning (banner de manutenção)
 *  - ?code= em qualquer rota → ativação (activeUser)
 *  - blocked → showWarning(legal_notice + descrição)   [getMe 1628]
 *  - inactive/not installed → showWarning(instructions_inactive) [getMe 1637]
 *  - conflit → showWaiting (aguardando confirmação)    [getMe 1631/1700]
 *  - aware/profile → onboarding em etapas após 1s | survey → avaliação após 1.5s
 *  - Watchdog (decisões 189 a 191): com a conta ativa, abre a sessão do computador; computador
 *    novo, recusado, no limite ou com outro em uso vê a tela do Watchdog no lugar do painel;
 *    se outro computador pega a vez com o painel aberto, a janela fica por cima
 * A Home (Versão 3B) tira a classe wrapper do #content e a barra de licença do rodapé.
 *
 * Movimento (23/10/2026, guia/10 "Barra do painel e troca de página"): ao trocar de página, o
 * conteúdo novo entra no lugar com fade de 140 ms (sem deslizar, sem View Transitions); se a
 * página demora, a barra fina do topo (TopProgress) cobre a espera.
 */
import { Suspense, lazy, useEffect, useLayoutEffect, useRef } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { animate } from 'motion/react';
import { useMe } from '../../api/useMe';
import { useCaptureMessages } from '../../hooks/useCaptureMessages';
import { useCaptureIngest } from '../../hooks/useCaptureIngest';
import { useExtensionPresence } from '../../hooks/useExtensionPresence';
import { useListBackupQueue } from '../../hooks/useListBackupQueue';
import { useRedeCloudSync } from '../../hooks/useRedeCloudSync';
import { usePlacePhoneRepair } from '../../hooks/usePlacePhoneRepair';
import { readableLimitLifted, useSealedListsRefresh } from '../../hooks/useSealedListsRefresh';
import { useSessionStore } from '../../stores/sessionStore';
import { useCaptureStore } from '../../stores/captureStore';
import { Header } from './Header';
import { ExpireBanner } from './ExpireBanner';
import { SurveyModal } from '../../features/survey/SurveyModal';
import { CaptureQueueNotice } from '../extension/CaptureQueueNotice';
import { ExtensionInstallModal } from '../extension/ExtensionInstallModal';
import { ExtensionReconnectModal } from '../extension/ExtensionReconnectModal';
import { ShellSkeleton } from '../common/Skeleton';
import { TopProgress } from '../common/TopProgress';
import { prefetchIdle } from '../../routes/prefetch';
import { BlockedScreen, InactiveScreen, MaintenanceScreen, MobileScreen } from '../common/WarningScreen';
import { LeadExplorerHost } from '../../features/leadexplorer/LeadExplorerHost';
import { AmbassadorHost } from '../../features/ambassador/AmbassadorHost';
import { TipToast } from '../../features/capture/TipToast';
import { AssistantHost } from '../../features/assistant/AssistantHost';
import { CTADialog } from '../../features/account/CTADialog';
import { OnboardingHost } from '../../features/onboarding/OnboardingHost';
import { GuiaHost } from '../../features/guia/GuiaHost';
import { BoasVindasHost } from '../../features/boasVindas/BoasVindasHost';
import { DeleteToastHost } from '../../features/lists/DeleteToastHost';
import { PlayerHost } from '../../features/player/PlayerHost';
import { WaitingDialog } from '../../features/account/WaitingDialog';
import { MAINTENANCE_MODE } from '../../config/env';
import { setCookie } from '../../lib/cookies';
import { flushLeadTalk } from '../../api/useLeadTalk';
import { decrypt } from '../../lib/crypt';
import { emitActivity } from '../../lib/activity';
import { LIST_MODULES, type ListModule } from '../../config/constants';
import { t as transicao } from '../../lib/movimento';
import { useWatchdogStore, watchdogAllowsApp } from '../../stores/watchdogStore';
import { useWatchdogBoot } from '../../features/account/useWatchdog';
import { usePanelLanguage } from '../../features/account/usePanelLanguage';
import { RequestNotice, SwitchedDialog, WatchdogGate } from '../../features/account/WatchdogScreens';

// Janela de captura: OVERLAY global sobre a tela atual (não é rota — o fundo continua
// montado). Lazy para manter o Leaflet do mapa fora do bundle principal.
const CaptureWindow = lazy(() =>
  import('../../pages/CapturePage').then((m) => ({ default: m.CapturePage })),
);

/** O aviso de conta bloqueada é desenhado a cada render; o evento sai uma vez por carga. */
let blockedReported = false;
function reportBlockedOnce(): void {
  if (blockedReported) return;
  blockedReported = true;
  emitActivity('account.blocked', { reason: 'blocked' });
}

export function AppShell(): JSX.Element {
  const me = useMe();
  const location = useLocation();
  const navigate = useNavigate();
  const session = useSessionStore((s) => s.session);
  const captureWindow = useCaptureStore((s) => s.captureWindow);
  useCaptureMessages(); // sincroniza listas/contadores com a captura da extensão
  useCaptureIngest(); // confirma lotes do fluxo local somente depois da gravação no IndexedDB
  useExtensionPresence(); // handshake vivo: presença + capability, sem ativar o rollout
  useListBackupQueue(session?.userId); // boot + online retomam toda lista da conta com dirtyAt
  useRedeCloudSync(session?.userId); // cópia da Rede do WhatsApp no servidor, em lotes (decisão 468)
  usePlacePhoneRepair(session?.userId); // uma vez: telefones de negócios gravados sem o país
  // Depois da compra, as listas do teste com registros fechados são lidas de novo (decisão 293).
  useSealedListsRefresh(session?.userId, readableLimitLifted(session));

  // Watchdog: só com a conta ativa no /app/me. Fora do ar, segue sem ele (fase `off`).
  const accountReady = !me.isLoading && Boolean(me.data?.id) && me.data?.status === 'active';
  useWatchdogBoot(accountReady);
  const watchdogPhase = useWatchdogStore((s) => s.phase);
  const pausedWhileUsing = useWatchdogStore((s) => s.pausedWhileUsing);
  // Idioma escolhido em Conta > Preferências: depois que o computador pode usar a v2.
  usePanelLanguage(accountReady && watchdogAllowsApp(watchdogPhase));

  // Telemetria de escuta acumulada do LeadTalk/LeadMusic (checkLeadTalk [2285]).
  useEffect(() => {
    if (me.data?.id) void flushLeadTalk();
  }, [me.data?.id]);

  // Atalho criptografado das notificações da extensão (openShortcut [9114]): decripta o
  // ?shortcut=<crypt>, abre a captura do módulo indicado e limpa o parâmetro da URL.
  useEffect(() => {
    if (!me.data?.id) return;
    const raw = new URLSearchParams(window.location.search).get('shortcut');
    if (!raw) return;
    try {
      const json = JSON.parse(decrypt(raw)) as { module?: string };
      if (json.module && (LIST_MODULES as readonly string[]).includes(json.module)) {
        useCaptureStore.getState().openCaptureWindow(json.module as ListModule);
      }
    } catch {
      /* atalho inválido — ignora */
    }
    const url = new URL(window.location.href);
    url.searchParams.delete('shortcut');
    window.history.replaceState({}, '', url.toString());
  }, [me.data?.id]);

  // Com a página pronta e o navegador ocioso, adianta o globo da Home e as telas mais usadas.
  useEffect(() => prefetchIdle(), []);

  // Movimento (23/10/2026): troca de página no lugar. A página nova entra com fade de 140 ms
  // (curva de entrada; 120 ms com "reduzir movimento", só opacidade). Antes da pintura, para a
  // página nova não aparecer inteira e piscar. Não anima a abertura nem mudança só de ?query.
  const contentRef = useRef<HTMLDivElement | null>(null);
  const paginaRef = useRef(location.pathname);
  useLayoutEffect(() => {
    if (paginaRef.current === location.pathname) return undefined;
    paginaRef.current = location.pathname;
    const el = contentRef.current;
    const entrada = transicao.entra('--duracao-140');
    if (!el || !entrada.duration) return undefined;
    const animacao = animate(el, { opacity: [0, 1] }, entrada);
    // Trocou de novo no meio: a anterior termina no fim (opacidade 1) e a nova começa.
    return () => animacao.complete();
  }, [location.pathname]);

  // Manutenção programada (offlineWarning): tela cheia, sem navegação.
  if (MAINTENANCE_MODE) {
    return <MaintenanceScreen />;
  }

  // Deep-link de ativação em QUALQUER rota (legado: parameters.code no startup [1530]).
  const activationCode = new URLSearchParams(location.search).get('code');
  if (activationCode && activationCode.length > 10 && location.pathname !== '/activate') {
    return <Navigate to={`/activate?code=${encodeURIComponent(activationCode)}`} replace />;
  }

  if (me.isLoading) {
    return <ShellSkeleton />;
  }

  // Erros de negócio conhecidos → onboarding (showOnboarding).
  const code = (me.error as { code?: string } | null)?.code;
  if (code === 'user_not_found') {
    return <Navigate to="/onboarding" replace />;
  }

  const status = me.data?.status;
  // showWarning: bloqueado (aviso legal) — sem navegação [getMe 1628].
  if (status === 'blocked') {
    reportBlockedOnce();
    return <BlockedScreen />;
  }
  // showWarning: conta inativa / nunca instalada [getMe 1636: trata os dois status igual].
  if (status === 'inactive' || status === 'not installed') {
    return <InactiveScreen />;
  }
  // pending sem ?code= → onboarding (getMe 1642-1648: cai no showOnboarding; o signup de lá
  // devolve pending de novo e mostra o "confirme seu email"). Com ?code=, o deep-link
  // /activate acima já capturou.
  if (status === 'pending') {
    return <Navigate to="/onboarding" replace />;
  }
  // not found → limpa a credencial e recomeça (getMe 1631-1635: setCookie vazio + showInstall;
  // aqui o recomeço é o /onboarding — a instalação da extensão tem modal próprio).
  if (status === 'not found') {
    setCookie('userId', '');
    setCookie('email', '');
    return <Navigate to="/onboarding" replace />;
  }
  // showWaiting: conflito de conta / aguardando confirmação de email [getMe 1631/1700].
  if (status === 'conflit') {
    return (
      <WaitingDialog
        email={session?.email ?? me.data?.email ?? ''}
        onReactivate={() => navigate('/onboarding')}
      />
    );
  }

  // Watchdog: enquanto confere este computador, o esqueleto; sem autorização ou sem a vez, a tela
  // dele. Perder a vez com o painel aberto não troca a tela: a janela abaixo fica por cima.
  if (accountReady && (watchdogPhase === 'idle' || watchdogPhase === 'checking')) {
    return <ShellSkeleton />;
  }
  if (
    accountReady &&
    !watchdogAllowsApp(watchdogPhase) &&
    !(watchdogPhase === 'paused' && pausedWhileUsing)
  ) {
    return <WatchdogGate phase={watchdogPhase} />;
  }

  // A Home (Versão 3B) ocupa a largura toda e mostra a licença no selo do topo, no lugar da
  // barra do rodapé, que segue nas outras páginas.
  const isHome = location.pathname === '/';

  return (
    <>
      <TopProgress />
      <Header />
      {/* Sem a faixa "Instale a extensão" abaixo do cabeçalho desde 27/10/2026 (pedido do
          Marcelo): quem tenta capturar sem a extensão vê a janela de instalação. */}
      <CaptureQueueNotice />
      <main>
        <div id="content" ref={contentRef} className={isHome ? undefined : 'wrapper'}>
          <Outlet />
        </div>
      </main>
      {isHome ? null : <ExpireBanner />}
      <div className="mobile as-mobile-host">
        <MobileScreen />
      </div>
      <OnboardingHost />
      <GuiaHost />
      <BoasVindasHost />
      <DeleteToastHost />
      <SurveyModal />
      <CTADialog />
      <ExtensionInstallModal />
      <ExtensionReconnectModal />
      <LeadExplorerHost />
      <AmbassadorHost />
      <TipToast />
      <AssistantHost />
      {captureWindow ? (
        <Suspense fallback={null}>
          <CaptureWindow />
        </Suspense>
      ) : null}
      {/* Player do LeadTalk/LeadMusic: fora das rotas para seguir tocando ao navegar. */}
      <PlayerHost />
      {watchdogPhase === 'paused' && pausedWhileUsing ? <SwitchedDialog /> : null}
      {watchdogPhase === 'active' ? <RequestNotice /> : null}
    </>
  );
}
